import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { getGallery } from '../services/api';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent } from './ui/dialog';
import { X, ChevronLeft, ChevronRight, Images, Loader2 } from 'lucide-react';

const GalleryPage = () => {
  const { language } = useLanguage();
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const data = await getGallery();
        setGalleryImages(data);
      } catch (error) {
        console.error('Error fetching gallery:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  const getText = (image) => {
    return language === 'pa' ? (image.caption_pa || image.caption_en) : image.caption_en;
  };

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setSelectedImage(galleryImages[index]);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToPrevious = () => {
    const newIndex = currentIndex === 0 ? galleryImages.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setSelectedImage(galleryImages[newIndex]);
  };

  const goToNext = () => {
    const newIndex = currentIndex === galleryImages.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    setSelectedImage(galleryImages[newIndex]);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Hero */}
      <section className="bg-gradient-to-r from-amber-500 to-orange-500 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {language === 'pa' ? 'ਫੋਟੋ ਗੈਲਰੀ' : 'Photo Gallery'}
            </h1>
            <p className="text-amber-100">
              {language === 'pa' ? 'ਪਿੰਡ ਦੀਆਂ ਤਸਵੀਰਾਂ ਅਤੇ ਯਾਦਾਂ' : 'Photos and memories from our village'}
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
          </div>
        ) : (
          <>
            {/* Gallery Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {galleryImages.map((image, index) => (
                <Card 
                  key={image.id} 
                  className="group border-0 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer"
                  onClick={() => openLightbox(index)}
                >
                  <CardContent className="p-0 relative">
                    <div className="aspect-[4/3] overflow-hidden">
                      <img 
                        src={image.url} 
                        alt={getText(image)}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                      <p className="text-white p-4 text-sm font-medium">
                        {getText(image)}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {galleryImages.length === 0 && !loading && (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-4">
                  <Images className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {language === 'pa' ? 'ਕੋਈ ਤਸਵੀਰ ਨਹੀਂ' : 'No photos available'}
                </h3>
              </div>
            )}
          </>
        )}
      </div>

      {/* Lightbox */}
      <Dialog open={!!selectedImage} onOpenChange={closeLightbox}>
        <DialogContent className="max-w-5xl bg-black/95 border-0 p-0">
          <div className="relative">
            <button 
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            >
              <X className="h-6 w-6 text-white" />
            </button>
            
            <button 
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            >
              <ChevronLeft className="h-6 w-6 text-white" />
            </button>
            
            <button 
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            >
              <ChevronRight className="h-6 w-6 text-white" />
            </button>

            {selectedImage && (
              <div className="flex flex-col items-center">
                <img 
                  src={selectedImage.url} 
                  alt={getText(selectedImage)}
                  className="max-h-[80vh] w-auto object-contain"
                />
                <p className="text-white text-center py-4 text-lg">
                  {getText(selectedImage)}
                </p>
                <p className="text-gray-400 text-sm pb-4">
                  {currentIndex + 1} / {galleryImages.length}
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GalleryPage;
